import {
  locationContainer,
  normalizeApiParams,
  statusContainer,
  utils,
} from '@wallaby-ui/core';
import {isArray, isEmpty, pick} from 'lodash';

import {getPrefixFromStateId} from './filter_and_search_helpers';
import {areAllComponentsLoaded} from './filter_and_search_selectors';
import {filterAndSearchSlice} from './filter_and_search_slice';
import {LIBRARY_NAME} from 'src/js/common/constants';
import {
  GRID_SORT_MODEL_SORT_COLUMN,
  GRID_SORT_MODEL_SORT_DIRECTION,
} from 'src/js/containers/grid/grid_constants';
// import infectionsMock from 'src/js/model/infections_mock.json';
import SearchDataFetcher from 'src/js/fetchers/search_data_fetcher';
import {AppThunk} from 'src/js/store';

const logger = utils.LogUtils.getLogger('FilterAndSearchActions', LIBRARY_NAME);

export function filterAndSearch(
  stateId,
  requestUrl,
  params,
  options = {}
): AppThunk {
  const searchResultPromise = SearchDataFetcher.search(requestUrl, params);
  return (dispatch, getState) => {
    dispatch(filterAndSearchSlice.actions.search(stateId, {options}));
    return searchResultPromise
      .then((response) => {
        const state = getState();
        const caseType =
          options.caseType === undefined &&
          !isEmpty(state[stateId].queryParameters.caseType)
            ? state[stateId].queryParameters.caseType
            : options.caseType;
        const enhancedOptions = {...options, caseType};
        dispatch(
          filterAndSearchSlice.actions.searchSuccess(stateId, {
            options: enhancedOptions,
            response,
            dynamicResultItemsModels: state[stateId].dynamicResultItemsModels,
          })
        );
      })
      .catch((error) => {
        logger.error(`SEARCH_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(
            error,
            filterAndSearchSlice.actions.searchFailure.type
          )
        );
        dispatch(filterAndSearchSlice.actions.searchFailure(stateId));
      });
  };
}

function encodeSortParams(params) {
  if (params.sort && isArray(params.sort) && params.sort.length > 0) {
    const sortParams = params.sort
      .map(
        (sortItem) =>
          `${sortItem[GRID_SORT_MODEL_SORT_COLUMN]},${sortItem[GRID_SORT_MODEL_SORT_DIRECTION]}`
      )
      .join('&');
    return {...params, sort: sortParams};
  }
  return params;
}

export function changeQueryParams(
  stateId,
  params,
  prefix?: string | undefined
): AppThunk {
  const encodedSortParams = encodeSortParams(params);
  return (dispatch) => {
    const prefixParams = locationContainer.encodeApiParameters(
      encodedSortParams,
      prefix
    );
    dispatch(
      filterAndSearchSlice.actions.changeQueryParams(stateId, prefixParams)
    );
    dispatch(
      filterAndSearchSlice.actions.changeApiParams(
        stateId,
        normalizeApiParams(params)
      )
    );
    // store new search params in the location state
    return dispatch(
      locationContainer.changeParams(
        locationContainer.LOCATION_REDUCER_STATE_ID,
        {...encodedSortParams},
        false,
        false,
        prefix
      )
    );
  };
}

export function resetQueryParams(stateId, paramsNames): AppThunk {
  return (dispatch, getState) =>
    Promise.all([
      dispatch(
        filterAndSearchSlice.actions.resetQueryParams(stateId, paramsNames)
      ),
    ]).then(() => {
      // store new search params in the location state
      const newState = getState()[stateId];
      return dispatch(
        locationContainer.changeParams(
          locationContainer.LOCATION_REDUCER_STATE_ID,
          {...pick(newState.queryParameters, paramsNames)},
          false,
          false,
          getPrefixFromStateId(stateId)
        )
      );
    });
}

export function changeParamsAndQuery(
  stateId,
  requestUrl,
  params,
  options = {}
): AppThunk {
  return (dispatch, getState) =>
    Promise.all([
      dispatch(filterAndSearchSlice.actions.changeQueryParams(stateId, params)),
      dispatch(
        filterAndSearchSlice.actions.changeApiParams(
          stateId,
          normalizeApiParams(params)
        )
      ),
    ]);
  // TODO: handle this
  /* .then(() =>
      dispatch(
        filterAndSearch(
          stateId,
          requestUrl,
          getState()[stateId].queryParameters,
          options
        )
      )
    ); */
}

export function exportData(
  stateId,
  requestUrl,
  params,
  exportRequestMethod,
  emptyExportMessage
): AppThunk {
  return (dispatch, getState) => {
    dispatch(filterAndSearchSlice.actions.exportData(stateId));
    const newParams = {...getState()[stateId].queryParameters, ...params};

    return SearchDataFetcher.exportData(
      requestUrl,
      newParams,
      exportRequestMethod,
      emptyExportMessage
    )
      .then((exportRowsLimitReached) => {
        dispatch(filterAndSearchSlice.actions.exportDataSuccess(stateId));
        if (exportRowsLimitReached) {
          dispatch(
            filterAndSearchSlice.actions.setExportRowsLimitReached(
              stateId,
              exportRowsLimitReached
            )
          );
        }
      })
      .catch((error) => {
        logger.error(`EXPORT_DATA_FAILURE: ${error}`);
        dispatch(
          statusContainer.addError(
            error,
            filterAndSearchSlice.actions.exportDataFailure.type
          )
        );
        dispatch(
          filterAndSearchSlice.actions.exportDataFailure(stateId, {error})
        );
      });
  };
}

export function loadStateFromParams(stateId, requestUrl): AppThunk {
  return (dispatch, getState) => {
    let resultPromise;
    const state = getState();

    // send artificial props to selector
    if (areAllComponentsLoaded(state, {filterAndSearchStateId: stateId})) {
      resultPromise = Promise.all([
        // dispatch(
        //   filterAndSearch(stateId, requestUrl, state[stateId].queryParameters)
        // ),
        dispatch(filterAndSearchSlice.actions.locationParsed(stateId, true)),
        dispatch(filterAndSearchSlice.actions.resetReload(stateId, false)),
      ]);
    } else {
      resultPromise = Promise.resolve();
    }

    return resultPromise;
  };
}

export function initComponents(stateId, components, requestUrl): AppThunk {
  return (dispatch) =>
    Promise.all([
      dispatch(
        filterAndSearchSlice.actions.initComponents(stateId, components)
      ),
    ]).then(() => dispatch(loadStateFromParams(stateId, requestUrl)));
}

export function changeServiceUrls(stateId, values): AppThunk {
  return (dispatch) =>
    dispatch(filterAndSearchSlice.actions.changeServiceUrls(stateId, values));
}

export function setDynamicResultItems(stateId, dynamicResultItems): AppThunk {
  return (dispatch) =>
    dispatch(
      filterAndSearchSlice.actions.setDynamicResultItems(
        stateId,
        dynamicResultItems
      )
    );
}

export function setDefaultQueryParams(
  stateId,
  defaultQueryParams,
  paramsPrefix?: string
): AppThunk {
  return changeQueryParams(stateId, defaultQueryParams, paramsPrefix);
}
